import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Map } from 'lucide-react';
import { useTour } from '../../contexts/TourContext';
import ProductTour from '../Tour/ProductTour';
import { iftaTour } from '../../tours/tourConfigs';
import LogTripModal from './LogTripModal';
import API_URL from '../../config';
import '../../App.css';

const getQuarterDateRange = (year, quarter) => {
  const startMonth = (quarter - 1) * 3;
  const endMonth = startMonth + 2;
  const start = new Date(year, startMonth, 1).toISOString().split('T')[0];
  const end = new Date(year, endMonth + 1, 0).toISOString().split('T')[0];
  return { start, end };
};

const IFTAPage = () => {
  const { isTourComplete, markTourComplete } = useTour();
  const iftaTourDone = isTourComplete('ifta');
  const [showTour, setShowTour] = useState(false);
  const [trips, setTrips] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLogModal, setShowLogModal] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [expandedTrip, setExpandedTrip] = useState(null);

  useEffect(() => {
    if (isLoading || iftaTourDone) {
      return undefined;
    }
    const t = setTimeout(() => setShowTour(true), 400);
    return () => clearTimeout(t);
  }, [isLoading, iftaTourDone]);

  const handleTourFinish = () => {
    setShowTour(false);
    markTourComplete('ifta');
  };

  useEffect(() => {
    const month = new Date().getMonth() + 1;
    let currentQuarter = 1;
    if (month <= 3) currentQuarter = 1;
    else if (month <= 6) currentQuarter = 2;
    else if (month <= 9) currentQuarter = 3;
    else currentQuarter = 4;
    setSelectedQuarter(currentQuarter);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      // Fetch quarterly report
      const reportResponse = await axios.get(
        `${API_URL}/api/ifta/report/${selectedYear}/${selectedQuarter}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSummary(reportResponse.data);
      setTrips(reportResponse.data.trips || []);

      // Also fetch all trips for the date range
      const { start, end } = getQuarterDateRange(selectedYear, selectedQuarter);
      const tripsResponse = await axios.get(
        `${API_URL}/api/ifta/trips?startDate=${start}&endDate=${end}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTrips(tripsResponse.data);
    } catch (err) {
      console.error('Error fetching IFTA data:', err);
      if (err.response?.status === 404) {
        setSummary(null);
        setTrips([]);
      } else {
        setError('Failed to load IFTA data');
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedYear, selectedQuarter]);

  useEffect(() => {
    if (selectedYear && selectedQuarter) {
      fetchData();
    }
  }, [selectedYear, selectedQuarter, fetchData]);

  const getCurrentQuarter = () => {
    const month = new Date().getMonth() + 1;
    const year = new Date().getFullYear();
    let quarter = 1;
    if (month <= 3) quarter = 1;
    else if (month <= 6) quarter = 2;
    else if (month <= 9) quarter = 3;
    else quarter = 4;
    return { year, quarter };
  };

  const handleSetCurrentQuarter = () => {
    const { year, quarter } = getCurrentQuarter();
    setSelectedYear(year);
    setSelectedQuarter(quarter);
  };

  const handleDeleteTrip = async (tripId) => {
    if (!window.confirm('Are you sure you want to delete this trip?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/ifta/trips/${tripId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (err) {
      console.error('Error deleting trip:', err);
      alert('Failed to delete trip');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/ifta/export/${selectedYear}/${selectedQuarter}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ifta-report-${selectedYear}-Q${selectedQuarter}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting report:', err);
      alert('Failed to export report');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const getMaxMiles = () => {
    if (!summary?.milesByState) return 1;
    return Math.max(...Object.values(summary.milesByState));
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading IFTA data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <ProductTour
        steps={iftaTour}
        run={showTour}
        onFinish={handleTourFinish}
        tourName="ifta"
      />
      
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1 className="page-title">IFTA Mileage Tracking</h1>
            <p className="page-subtitle">Track mileage and fuel for quarterly IFTA reporting</p>
          </div>
          <button
            type="button"
            className="btn btn-primary log-trip-button"
            onClick={() => setShowLogModal(true)}
          >
            + Log New Trip
          </button>
        </div>
      </div>

      {error && (
        <div className="dashboard-error">
          <p>{error}</p>
        </div>
      )}

      {/* Quarter Selector */}
      <div className="quarter-selector">
        <div className="selector-group">
          <label>Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {[selectedYear - 1, selectedYear, selectedYear + 1].map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div className="selector-group">
          <label>Quarter:</label>
          <select
            value={selectedQuarter || ''}
            onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
          >
            <option value="1">Q1 (Jan-Mar)</option>
            <option value="2">Q2 (Apr-Jun)</option>
            <option value="3">Q3 (Jul-Sep)</option>
            <option value="4">Q4 (Oct-Dec)</option>
          </select>
        </div>
        <button className="btn btn-secondary" onClick={handleSetCurrentQuarter}>
          Current Quarter
        </button>
        <button type="button" className="btn btn-primary export-csv-button" onClick={handleExport}>
          Export CSV
        </button>
      </div>

      {summary ? (
        <>
          {/* Summary Cards */}
          <div className="ifta-stats-grid" style={{ marginTop: '30px' }}>
            <div className="ifta-stat-card">
              <div className="ifta-stat-value">{formatNumber(summary.totalMiles)}</div>
              <div className="ifta-stat-label">Total Miles</div>
            </div>
            <div className="ifta-stat-card">
              <div className="ifta-stat-value">{summary.totalTrips}</div>
              <div className="ifta-stat-label">Total Trips</div>
            </div>
            <div className="ifta-stat-card">
              <div className="ifta-stat-value">
                {Object.keys(summary.milesByState || {}).length}
              </div>
              <div className="ifta-stat-label">States Traveled</div>
            </div>
            <div className="ifta-stat-card">
              <div className="ifta-stat-value">{formatNumber(summary.totalFuel)}</div>
              <div className="ifta-stat-label">Fuel (Gallons)</div>
            </div>
          </div>

          {/* State Breakdown Table */}
          {summary.milesByState && Object.keys(summary.milesByState).length > 0 && (
            <div className="state-breakdown-section">
              <h2 className="section-title">State Breakdown</h2>
              <table className="state-breakdown-table">
                <thead>
                  <tr>
                    <th>State</th>
                    <th>Miles</th>
                    <th>Fuel (Gallons)</th>
                    <th>Visual</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(summary.milesByState)
                    .sort((a, b) => b[1] - a[1])
                    .map(([state, miles]) => {
                      const fuel = summary.fuelByState?.[state]?.gallons || 0;
                      const maxMiles = getMaxMiles();
                      const percentage = (miles / maxMiles) * 100;
                      return (
                        <tr key={state}>
                          <td><strong>{state}</strong></td>
                          <td>{formatNumber(miles)}</td>
                          <td>{formatNumber(fuel)}</td>
                          <td>
                            <div className="state-bar" style={{ width: `${percentage}%` }}></div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}

          {/* Trips Table */}
          <div className="trips-section">
            <h2 className="section-title">Trips</h2>
            <table className="trips-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Miles</th>
                  <th>States</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {trips.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                      No trips logged for this quarter
                    </td>
                  </tr>
                ) : (
                  trips.map((trip) => {
                    const states = Object.keys(trip.mileageByState || {});
                    const isExpanded = expandedTrip === trip.id;
                    return (
                      <React.Fragment key={trip.id}>
                        <tr
                          className="trip-row"
                          onClick={() => setExpandedTrip(isExpanded ? null : trip.id)}
                        >
                          <td>{formatDate(trip.tripDate)}</td>
                          <td>{trip.startLocation}</td>
                          <td>{trip.endLocation}</td>
                          <td>{formatNumber(trip.totalMiles)}</td>
                          <td>{states.join(', ')}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => {
                                setEditingTrip(trip);
                                setShowLogModal(true);
                              }}
                              style={{ marginRight: '5px' }}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteTrip(trip.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan="6" style={{ backgroundColor: '#2a2a2a', padding: '20px' }}>
                              <div className="trip-details">
                                <h4>Trip Details</h4>
                                <div className="detail-grid">
                                  <div>
                                    <strong>States & Mileage:</strong>
                                    <ul>
                                      {Object.entries(trip.mileageByState || {}).map(
                                        ([state, miles]) => (
                                          <li key={state}>
                                            {state}: {formatNumber(miles)} miles
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                  {trip.fuelPurchases && trip.fuelPurchases.length > 0 && (
                                    <div>
                                      <strong>Fuel Purchases:</strong>
                                      <ul>
                                        {trip.fuelPurchases.map((purchase, idx) => (
                                          <li key={idx}>
                                            {purchase.state}: {purchase.gallons} gal @ $
                                            {purchase.price}/gal - {purchase.location}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {trip.notes && (
                                    <div>
                                      <strong>Notes:</strong>
                                      <p>{trip.notes}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="empty-state-enhanced">
          <div className="empty-icon"><Map size={48} color="var(--accent)" /></div>
          <h3>No trips logged for Q{selectedQuarter} {selectedYear}</h3>
          <p>Log your first IFTA trip to begin tracking mileage and fuel purchases for quarterly reporting.</p>
          <div className="empty-cta">
            <button
              className="btn btn-primary"
              onClick={() => setShowLogModal(true)}
            >
              Log First Trip
            </button>
          </div>
        </div>
      )}

      {showLogModal && (
        <LogTripModal
          isOpen={showLogModal}
          onClose={() => {
            setShowLogModal(false);
            setEditingTrip(null);
          }}
          onSuccess={() => {
            fetchData();
            setShowLogModal(false);
            setEditingTrip(null);
          }}
          initialTrip={editingTrip}
        />
      )}
    </div>
  );
};

export default IFTAPage;

